// IndexedDB offline storage for PWA — caches devices, hubs, configs, and queues commands
const DB_NAME = 'baraqubot-offline'
const DB_VERSION = 1

const STORES = {
    devices: 'devices',
    hubs: 'hubs',
    configs: 'configs',
    commandQueue: 'commandQueue',
    hubAddresses: 'hubAddresses',
    meta: 'meta',
}

let dbInstance = null

function openDb() {
    if (dbInstance) return Promise.resolve(dbInstance)

    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION)

        req.onupgradeneeded = (e) => {
            const db = e.target.result
            if (!db.objectStoreNames.contains(STORES.devices)) {
                db.createObjectStore(STORES.devices, { keyPath: 'mac' })
            }
            if (!db.objectStoreNames.contains(STORES.hubs)) {
                db.createObjectStore(STORES.hubs, { keyPath: '_id' })
            }
            if (!db.objectStoreNames.contains(STORES.configs)) {
                db.createObjectStore(STORES.configs, { keyPath: 'key' })
            }
            if (!db.objectStoreNames.contains(STORES.commandQueue)) {
                const store = db.createObjectStore(STORES.commandQueue, { keyPath: 'id', autoIncrement: true })
                store.createIndex('status', 'status', { unique: false })
                store.createIndex('createdAt', 'createdAt', { unique: false })
            }
            if (!db.objectStoreNames.contains(STORES.hubAddresses)) {
                db.createObjectStore(STORES.hubAddresses, { keyPath: 'hubId' })
            }
            if (!db.objectStoreNames.contains(STORES.meta)) {
                db.createObjectStore(STORES.meta, { keyPath: 'key' })
            }
        }

        req.onsuccess = (e) => {
            dbInstance = e.target.result
            resolve(dbInstance)
        }
        req.onerror = (e) => reject(e.target.error)
    })
}

async function tx(storeName, mode = 'readonly') {
    const db = await openDb()
    const transaction = db.transaction(storeName, mode)
    return transaction.objectStore(storeName)
}

function promisify(request) {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
    })
}

// ---- Generic CRUD helpers ----

async function getAll(storeName) {
    const store = await tx(storeName)
    return promisify(store.getAll())
}

async function getByKey(storeName, key) {
    const store = await tx(storeName)
    return promisify(store.get(key))
}

async function put(storeName, value) {
    const store = await tx(storeName, 'readwrite')
    return promisify(store.put(value))
}

async function deleteByKey(storeName, key) {
    const store = await tx(storeName, 'readwrite')
    return promisify(store.delete(key))
}

async function clearStore(storeName) {
    const store = await tx(storeName, 'readwrite')
    return promisify(store.clear())
}

// ---- Device cache ----

export async function cacheDevices(devices) {
    const db = await openDb()
    const transaction = db.transaction(STORES.devices, 'readwrite')
    const store = transaction.objectStore(STORES.devices)
    for (const device of devices) {
        store.put(device)
    }
    return new Promise((resolve, reject) => {
        transaction.oncomplete = resolve
        transaction.onerror = () => reject(transaction.error)
    })
}

export async function getCachedDevices() {
    return getAll(STORES.devices)
}

export async function updateCachedDevice(device) {
    return put(STORES.devices, device)
}

// ---- Hub cache ----

export async function cacheHubs(hubs) {
    const db = await openDb()
    const transaction = db.transaction(STORES.hubs, 'readwrite')
    const store = transaction.objectStore(STORES.hubs)
    for (const hub of hubs) {
        store.put(hub)
    }
    return new Promise((resolve, reject) => {
        transaction.oncomplete = resolve
        transaction.onerror = () => reject(transaction.error)
    })
}

export async function getCachedHubs() {
    return getAll(STORES.hubs)
}

// ---- Hub LAN addresses ----

export async function saveHubAddress(hubId, ip, port = 80) {
    return put(STORES.hubAddresses, { hubId, ip, port, lastSeen: Date.now() })
}

export async function getHubAddress(hubId) {
    return getByKey(STORES.hubAddresses, hubId)
}

export async function getAllHubAddresses() {
    return getAll(STORES.hubAddresses)
}

// ---- Offline command queue ----

export async function enqueueCommand(command) {
    return put(STORES.commandQueue, {
        ...command,
        status: 'pending',
        createdAt: Date.now(),
        retries: 0,
    })
}

export async function getPendingCommands() {
    const all = await getAll(STORES.commandQueue)
    return all.filter(cmd => cmd.status === 'pending').sort((a, b) => a.createdAt - b.createdAt)
}

export async function markCommandSynced(id) {
    const cmd = await getByKey(STORES.commandQueue, id)
    if (cmd) {
        cmd.status = 'synced'
        cmd.syncedAt = Date.now()
        return put(STORES.commandQueue, cmd)
    }
}

export async function markCommandFailed(id, error) {
    const cmd = await getByKey(STORES.commandQueue, id)
    if (cmd) {
        cmd.status = 'failed'
        cmd.error = error
        cmd.retries = (cmd.retries || 0) + 1
        return put(STORES.commandQueue, cmd)
    }
}

export async function clearSyncedCommands() {
    const all = await getAll(STORES.commandQueue)
    const db = await openDb()
    const transaction = db.transaction(STORES.commandQueue, 'readwrite')
    const store = transaction.objectStore(STORES.commandQueue)
    for (const cmd of all) {
        if (cmd.status === 'synced') store.delete(cmd.id)
    }
    return new Promise((resolve, reject) => {
        transaction.oncomplete = resolve
        transaction.onerror = () => reject(transaction.error)
    })
}

// ---- Config / meta cache ----

export async function saveConfig(key, value) {
    return put(STORES.configs, { key, value, updatedAt: Date.now() })
}

export async function getConfig(key) {
    const record = await getByKey(STORES.configs, key)
    return record?.value ?? null
}

export async function saveMeta(key, value) {
    return put(STORES.meta, { key, value, updatedAt: Date.now() })
}

export async function getMeta(key) {
    const record = await getByKey(STORES.meta, key)
    return record?.value ?? null
}

// ---- Cleanup ----

export async function clearAll() {
    for (const storeName of Object.values(STORES)) {
        await clearStore(storeName)
    }
}

export default {
    cacheDevices, getCachedDevices, updateCachedDevice,
    cacheHubs, getCachedHubs,
    saveHubAddress, getHubAddress, getAllHubAddresses,
    enqueueCommand, getPendingCommands, markCommandSynced, markCommandFailed, clearSyncedCommands,
    saveConfig, getConfig, saveMeta, getMeta,
    clearAll,
}
